import { cloneDeep, forOwn, get, isEmpty, isString, omit, size } from 'lodash'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { string } from 'yup'

import Button from '@/components/Button'
import Form from '@/components/Form'
import ValidationWarning from '@/components/Form/ValidationWarning'

import AppFormFields from './AppFormFields'
import ConnectorFormFields from './ConnectorFormFields'
import messages from './messages'
import MicroUIFormFields from './MicroUIFormFields'
import { schemaValidation } from './schemaValidation'

import './index.scss'

import useFeatures from '@/components/useFeatures'
import { APP_STORE_EXPANDED_BY_DEFAULT } from '@/constants/featureToggles'

function CustomAppEditView(props: any) {
  const features = useFeatures()

  const onCancelImageCallback = useRef<Function>()
  const onCancelImageCallbackSmartNotification = useRef<Function>()

  const onDelete = () => {
    props.onDelete()
  }

  const setAllowDetailExpandToBool = (values: any) => {
    const allowDetailExpand = values.currentMicroapp.configuration.allowDetailExpand === 'true'
    values.currentMicroapp.configuration = {
      ...values.currentMicroapp.configuration,
      allowDetailExpand
    }
    return values
  }

  const handleMicroUIAudienceTarget = (values: any) => {
    if (values.isMicroUIExpanded && !values.isAudienceTargetingExpanded) {
      const defaultAudience = [
        {
          type: 'filter',
          value: 'active eq true'
        }
      ]
      values.currentMicroapp.audience = defaultAudience
    }
  }

  const omitValues = (values: any) => {
    return omit(values, ['currentMicroapp.configuration.newDomainAddress', 'currentMicroapp.defaultsSelected'])
  }

  const addMailto = (values: any) => {
    const emailSchema = string().email().required()
    const isEmail = emailSchema.isValidSync(values.currentMicroapp.configuration.contact.link)
    // check if an email address is passed in the Support Contact field
    if (isEmail) {
      const link = 'mailto:' + values.currentMicroapp.configuration.contact.link
      values.currentMicroapp.configuration.contact = {
        ...values.currentMicroapp.configuration.contact,
        link
      }
    }
  }

  const removeEmptyStrings = (obj: any) => {
    const prune = (current: any) => {
      forOwn(current, (value, key) => {
        if ((isString(value) && isEmpty(value)) || (key === 'currentApp' && isEmpty(prune(value)))) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete current[key]
        }
      })
      return current
    }
    return prune(cloneDeep(obj))
  }

  const onSubmit = (values: any) => {
    let newValues = omitValues(values)
    newValues = { ...removeEmptyStrings(newValues) }
    newValues = setAllowDetailExpandToBool(newValues)
    handleMicroUIAudienceTarget(newValues)
    addMailto(newValues)
    props.submitApp(newValues)
  }

  const getErrorsFromValidationError = (validationError: any) => {
    const FIRST_ERROR = 0
    return validationError.inner.reduce((errors: any, error: any) => {
      return {
        ...errors,
        [error.path]: error.errors[FIRST_ERROR].props.defaultMessage
      }
    }, {})
  }

  const onValidation = (values: any) => {
    const validationSchema = schemaValidation(values)
    try {
      validationSchema.validateSync(values, { abortEarly: false })
      return {}
    } catch (error) {
      return getErrorsFromValidationError(error)
    }
  }

  const getUnsignedUrl = (url: any) => {
    let unsignedUrl = ''
    if (url !== '') {
      const parsedUrl = new URL(url)
      unsignedUrl = parsedUrl.origin + parsedUrl.pathname
    }
    return unsignedUrl
  }

  const registerOnCancel = (cancelCallback: Function) => {
    onCancelImageCallback.current = cancelCallback
  }

  const registerOnCancelSmartNotification = (cancelCallback: Function) => {
    onCancelImageCallbackSmartNotification.current = cancelCallback
  }

  const cancel = () => {
    // First ask the image inputs to do what it needs because we are canceling!
    onCancelImageCallback.current?.()
    onCancelImageCallbackSmartNotification.current?.()
    props.onCancel()
  }

  const { actionMessage, currentApp, currentConnector, currentMicroapp, isActionInProgress, isEditing } = props

  return (
    <Form
      actionMessage={actionMessage}
      buttons={
        <>
          <Button color="secondary" onClick={cancel}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          {isEditing && (
            <Button color="warning" onClick={onDelete}>
              <FormattedMessage {...messages.delete} />
            </Button>
          )}
          <Button color="primary" type="submit" id="appAction">
            <FormattedMessage {...(isEditing ? messages.update : messages.install)} />
          </Button>
        </>
      }
      className="custom-app-form"
      enableReinitialize
      initialValues={{
        currentApp: {
          id: get(props, 'currentApp.id', ''),
          name: get(props, 'currentApp.name', ''),
          iconUrl: getUnsignedUrl(get(props, 'currentApp.iconUrl', '')),
          type: get(props, 'currentApp.type', 'internal')
        },
        currentMicroapp: {
          id: get(props, 'currentMicroapp.id', ''),
          title: get(props, 'currentMicroapp.title', ''),
          // Check the audience for both formats to see if there is a filter value,
          // if not set it to empty and let the audience targeting component handle setting default values
          audience:
            size(props.currentMicroapp.audience) !== 0 &&
            (props.currentMicroapp.audience[0].filters || props.currentMicroapp.audience[0].audienceFilter)
              ? props.currentMicroapp.audience
              : [],
          url: get(props, 'currentMicroapp.url', ''),
          summary: get(props, 'currentMicroapp.summary', ''),
          configuration: {
            allowDetailExpand: get(props, 'currentMicroapp.configuration.allowDetailExpand', false),
            ...(features[APP_STORE_EXPANDED_BY_DEFAULT] === true && {
              expandedByDefault: get(props, 'currentMicroapp.configuration.expandedByDefault', false)
            }),
            domainWhitelist: get(props, 'currentMicroapp.configuration.domainWhitelist', []),
            securityOptions: get(props, 'currentMicroapp.configuration.securityOptions', []),
            contact: get(props, 'currentMicroapp.configuration.contact', {
              link: get(props, 'currentMicroapp.configuration.contact.link', '')
            }),
            newDomainAddress: '',
            internal: get(props, 'currentMicroapp.configuration.internal', {})
          }
        },
        currentConnector: {
          id: get(props, 'currentConnector.id', ''),
          name: get(props, 'currentConnector.name', ''),
          webHookUrl: get(props, 'currentConnector.webHookUrl', ''),
          iconUrl: getUnsignedUrl(get(props, 'currentConnector.iconUrl', '')),
          clientCredential: get(props, 'currentConnector.clientCredential', {}),
          type: 'internal'
        },
        isSmartNotificationsExpanded: false,
        isMicroUIExpanded: false,
        isAudienceTargetingExpanded: false
      }}
      isActionInProgress={isActionInProgress}
      onReset={cancel}
      validate={onValidation}
      onSubmit={onSubmit}
      validationWarning={<ValidationWarning message={messages.validationWarning} />}
    >
      <div className="ref-guide-link">
        <a href="https://help.workgrid.com/s/article/Install-a-Custom-App" target="_blank" rel="noopener noreferrer">
          <FormattedMessage {...messages.referenceGuide} />
        </a>
      </div>
      <AppFormFields currentApp={currentApp} registerOnCancel={registerOnCancel} />
      <ConnectorFormFields
        currentConnector={currentConnector}
        onNavigateConnectorLogs={props.onNavigateConnectorLogs}
        registerOnCancelSmartNotification={registerOnCancelSmartNotification}
      />
      <MicroUIFormFields currentMicroapp={currentMicroapp} />
    </Form>
  )
}

CustomAppEditView.displayName = 'CustomAppEditView'

CustomAppEditView.propTypes = {
  actionMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  currentApp: PropTypes.shape({
    iconUrl: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string
  }),
  currentMicroapp: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    url: PropTypes.string,
    iconUrl: PropTypes.string,
    audience: PropTypes.array,
    configuration: PropTypes.shape({
      allowDetailExpand: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      expandedByDefault: PropTypes.bool,
      securityOptions: PropTypes.array,
      domainWhitelist: PropTypes.array,
      contact: PropTypes.shape({
        link: PropTypes.string
      })
    })
  }),
  currentConnector: PropTypes.shape({
    clientCredential: PropTypes.shape({
      clientId: PropTypes.string,
      clientSecret: PropTypes.string
    }),
    iconUrl: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    webHookUrl: PropTypes.string,
    appId: PropTypes.string
  }),
  onDelete: PropTypes.func.isRequired,
  submitApp: PropTypes.func.isRequired,
  isActionInProgress: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onNavigateConnectorLogs: PropTypes.func.isRequired
}

CustomAppEditView.defaultProps = {
  actionMessage: null,
  currentApp: {},
  currentMicroapp: {
    configuration: {}
  },
  currentConnector: {},
  isEditing: false,
  isCreateConnectorSuccessful: false
}

export { /** @deprecated */ CustomAppEditView }

export default CustomAppEditView
